import type {
  ChangeEvent,
  ComponentProps,
  KeyboardEventHandler,
  PropsWithChildren
} from 'react';
import type {
  Control,
  FieldValues,
  Path,
  UseFormRegister
} from 'react-hook-form';
import isBoolean from 'lodash/isBoolean';
import styled, { css } from 'styled-components';
import type { Merge, PlainObject } from 'simplytyped';

import { EditableTable } from '../../organisms/EditableTable';
import { InputCheck } from './InputCheck';
import { InputCurrency } from './InputCurrency';
import { InputDate } from './InputDate';
import { InputEmail } from './InputEmail';
import { InputNumber } from './InputNumber';
import { InputPassword } from './InputPassword';
import { InputRange } from './InputRange';
import { InputText } from './InputText';
import { Select } from './Select';
import { Textarea } from './Textarea';

export type InputArrayPrimitiveType =
  | 'text'
  | 'date'
  | 'range'
  | 'textarea'
  | 'select'
  | 'currency'
  | 'password'
  | 'number'
  | 'boolean'
  | 'list' // @TODO, implement or remove from primitive type
  | 'email';

export type InputWrapperProps<
  Schema extends FieldValues = FieldValues,
  TContext = any
> = Merge<
  {
    changeFunction?: (
      e: ChangeEvent<HTMLInputElement | HTMLSelectElement>
    ) => void;
    label?: string;
    id?: string;
    helpText?: string;
    errorMessage?: string;
    hideErrorMessage?: boolean;
    required?: boolean;
    disabled?: boolean;
    readOnly?: boolean;
    removeMargin?: boolean;
    inputSize?: string;
    step?: number;
    loading?: boolean;
    min?: string;
    max?: string;
    maxLength?: number;
    minLength?: number;
    options?: Array<string | number>;
    control?: Control<Schema, TContext>;
    tableConfig?: Omit<
      ComponentProps<typeof EditableTable>,
      'name' | 'control' | 'register'
    >;
    inputs?: Array<InputWrapperProps>; // for an array of objects, @TODO improve these typings
    complexOptions?: Array<{
      value: string | number;
      text: string | number;
    }>;
    onKeyUp?: KeyboardEventHandler<HTMLInputElement>;
    register?: UseFormRegister<Schema>;
    defaultValue?: string | number;
    autoFill?: boolean;
    autoFocus?: boolean;
  },
  | {
      type: 'table';
      value?: Array<PlainObject>;
      // name: ArrayPath<Schema>;
      name: string;
    }
  | {
      // name: Path<Schema>;
      name: string;
      value?: string | number | boolean;
      type: InputArrayPrimitiveType;
    }
>;

const Wrapper = styled.div<{ removeMargin: boolean }>(
  ({ theme, removeMargin }) => css`
    display: block;
    position: relative;
    margin-bottom: ${!removeMargin ? theme.margins.md : 0};
  `
);

export const InputLabel = styled.label<{ loading?: number }>(
  ({ loading }) => css`
    display: block;
    font-weight: 500;
    margin-bottom: 5px;

    ${loading &&
    css`
      cursor: wait;
      pointer-events: none;

      * {
        visibility: hidden;
      }

      &::after {
        display: block;
        visibility: visible;
        color: var(--font-color) !important;
        border: 3px solid rgba(0, 0, 0, 0.1);
        border-left-color: inherit;
        border-radius: 50%;
        width: 10px;
        height: 10px;
        position: absolute;
        right: 5px;
        top: 5px;
        transform: translate(50%, -50%);
        content: '';
        animation: spinner 1s infinite linear;
      }
    `}
  `
);

export const ErrorWrapper = styled.span(
  ({ theme }) => css`
    display: block;
    color: ${theme.colors.main.error};
    font-size: ${theme.fonts.sizes.h6};
    word-wrap: break-word;
    margin-top: 5px;
  `
);

export const RequiredSymbol = styled.span(
  ({ theme }) => css`
    color: ${theme.colors.main.error};
  `
);

export const HelpTextWrapper = styled.p(
  ({ theme }) => css`
    opacity: 0.75;
    font-size: ${theme.fonts.sizes.h6};
    margin-top: 0;
  `
);

export function getInput<Schema extends FieldValues = FieldValues>({
  id,
  value,
  type,
  required,
  disabled,
  readOnly,
  errorMessage,
  name,
  register,
  label,
  min,
  max,
  maxLength,
  minLength,
  step = 1,
  onKeyUp,
  changeFunction,
  options,
  complexOptions,
  inputSize = 'md',
  control = undefined,
  tableConfig = undefined,
  children,
  defaultValue,
  autoFocus,
  autoFill
}: PropsWithChildren<InputWrapperProps<Schema>>) {
  if (isBoolean(value) || type === 'boolean') {
    return (
      <InputCheck
        id={id ? `${id}` : undefined}
        name={name as Path<Schema>}
        value={value as boolean}
        register={register}
        label={label}
        inputSize={inputSize}
        disabled={disabled}
        errorMessage={errorMessage}
      />
    );
  }

  return (
    <>
      {type === 'text' && (
        <InputText
          id={id ? `${id}` : undefined}
          value={value}
          inputSize={inputSize}
          maxLength={maxLength}
          minLength={minLength}
          required={required}
          disabled={disabled}
          errorMessage={errorMessage}
          name={name as Path<Schema>}
          register={register}
          label={label}
          defaultValue={defaultValue}
          readOnly={readOnly}
          autoFill={autoFill}
        />
      )}
      {type === 'number' && (
        <InputNumber
          id={id ? `${id}` : undefined}
          value={value}
          inputSize={inputSize}
          required={required}
          disabled={disabled}
          min={min}
          max={max}
          step={step}
          errorMessage={errorMessage}
          name={name as Path<Schema>}
          register={register}
          label={label}
          defaultValue={defaultValue}
        />
      )}
      {type === 'password' && (
        <InputPassword
          id={id ? `${id}` : undefined}
          value={value}
          inputSize={inputSize}
          onKeyUp={onKeyUp}
          required={required}
          disabled={disabled}
          errorMessage={errorMessage}
          register={register}
          name={name as Path<Schema>}
          label={label}
          autoFill={autoFill}
        />
      )}
      {type === 'email' && (
        <InputEmail
          id={id ? `${id}` : undefined}
          value={value}
          inputSize={inputSize}
          name={name as Path<Schema>}
          onKeyUp={onKeyUp}
          required={required}
          disabled={disabled}
          errorMessage={errorMessage}
          register={register}
          label={label}
          defaultValue={
            typeof defaultValue === 'string' ? defaultValue : undefined
          }
          autoFill={autoFill}
        />
      )}
      {type === 'currency' && (
        <InputCurrency
          id={id ? `${id}` : undefined}
          value={value}
          inputSize={inputSize}
          required={required}
          disabled={disabled}
          errorMessage={errorMessage}
          name={name as Path<Schema>}
          register={register}
          changeFunction={changeFunction}
          defaultValue={defaultValue}
          autoFocus={autoFocus}
        />
      )}
      {type === 'range' && (
        <InputRange
          id={id ? `${id}` : undefined}
          value={value}
          step={step}
          inputSize={inputSize}
          required={required}
          disabled={disabled}
          errorMessage={errorMessage}
          name={name as Path<Schema>}
          register={register}
        />
      )}
      {type === 'date' && (
        <InputDate
          id={id ? `${id}` : undefined}
          value={value}
          inputSize={inputSize}
          changeFunction={changeFunction}
          name={name as Path<Schema>}
          required={required}
          disabled={disabled}
          min={min}
          max={max}
          errorMessage={errorMessage}
          register={register}
        />
      )}
      {type === 'table' && (
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        <EditableTable
          errorMessage={errorMessage}
          name={name as Path<Schema>}
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          control={control!}
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          register={register!}
          {...tableConfig}
        >
          {children}
        </EditableTable>
      )}
      {type === 'textarea' && (
        <Textarea
          required={required}
          inputSize={inputSize}
          disabled={disabled}
          errorMessage={errorMessage}
        />
      )}
      {type === 'select' && (options || complexOptions) && (
        <Select
          value={value}
          inputSize={inputSize}
          name={name as Path<Schema>}
          required={required}
          disabled={disabled}
          errorMessage={errorMessage}
          options={options}
          complexOptions={complexOptions}
          register={register}
          defaultValue={defaultValue}
          changeFunction={changeFunction}
        />
      )}
    </>
  );
}

export const InputWrapper = <Schema extends FieldValues>(
  props: PropsWithChildren<InputWrapperProps<Schema>>
) => {
  const {
    id,
    label,
    type,
    options,
    complexOptions,
    required,
    helpText,
    errorMessage,
    hideErrorMessage = false,
    loading = false,
    removeMargin = false
  } = props;

  return (
    <Wrapper removeMargin={removeMargin}>
      {label &&
        ((type === 'select' && (options || complexOptions)) ||
          !['select', 'boolean'].includes(type)) && (
          <>
            <InputLabel id={id ? `${id}-label` : undefined} loading={+loading}>
              {label}
              {required && <RequiredSymbol>*</RequiredSymbol>}
            </InputLabel>
            {helpText && <HelpTextWrapper>{helpText}</HelpTextWrapper>}
          </>
        )}
      {getInput(props)}
      {errorMessage && !hideErrorMessage && (
        <ErrorWrapper>{errorMessage}</ErrorWrapper>
      )}
    </Wrapper>
  );
};
